import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { fmt, r2, today } from "@/lib/format";
import { Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function FxPaymentDialog({ inv, open, onClose, onPaid }) {
  const { t } = useLang();
  const [f, setF] = useState({ payment_date: today(), payment_fx_rate: inv.fx_rate });
  const [src, setSrc] = useState("");
  const [busy, setBusy] = useState(false);
  const fetchRate = async (d) => {
    try {
      const { data } = await api.get("/fx", { params: { currency: inv.currency, date_: d } });
      setF((p) => ({ ...p, payment_fx_rate: data.rate }));
      setSrc(`${data.source} · ${data.date}`);
    } catch { setSrc(""); }
  };
  useEffect(() => { if (open) fetchRate(f.payment_date); }, [open]); // eslint-disable-line
  const received = r2(inv.fx_total * (f.payment_fx_rate || 0));
  const diff = r2(received - inv.total);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(`/invoices/${inv.id}/status`, { status: "paid", payment_date: f.payment_date, payment_fx_rate: Number(f.payment_fx_rate) });
      onPaid(data);
      toast.success(t("saved"));
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fx-payment-dialog">
        <DialogHeader><DialogTitle>{t("markPaid")} — {inv.number}</DialogTitle><DialogDescription>{t("fxPaymentText")}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label={t("paymentDate")}><Input data-testid="fx-payment-date" type="date" value={f.payment_date} onChange={(e) => { setF({ ...f, payment_date: e.target.value }); fetchRate(e.target.value); }} required /></Field>
          <Field label={`${t("fxRate")} (1 ${inv.currency} = ? AED)`}>
            <Input data-testid="fx-payment-rate" type="number" step="0.0001" min="0.0001" className="font-mono" value={f.payment_fx_rate} onChange={(e) => setF({ ...f, payment_fx_rate: e.target.value })} required />
            {src && <p className="mt-1 text-[11px] text-slate-400">{t("fxRateHint")} {src}</p>}
          </Field>
          <div className="rounded-md bg-slate-50 p-3 text-sm">
            <div className="flex justify-between text-slate-600"><span>{t("fxInvoiced")}</span><span className="font-mono">AED {fmt(inv.total)}</span></div>
            <div className="flex justify-between text-slate-600"><span>{t("fxReceived")}</span><span className="font-mono" data-testid="fx-payment-received">AED {fmt(received)}</span></div>
            <div className={`mt-1 flex justify-between border-t border-slate-200 pt-1 font-bold ${diff >= 0 ? "text-[#007A3D]" : "text-[#CE1126]"}`}><span>{t(diff >= 0 ? "fxGain" : "fxLoss")}</span><span className="font-mono" data-testid="fx-payment-diff">{diff >= 0 ? "+" : ""}{fmt(diff)} AED</span></div>
          </div>
          <Button data-testid="fx-payment-submit" disabled={busy} className="w-full bg-[#007A3D] hover:bg-[#005E2E]">{busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t("markPaid")}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
